import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { Observable, catchError, throwError, of, switchMap } from 'rxjs';

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const tokenUrl = '/protocol/openid-connect/token';

    if (req.url.includes(tokenUrl)) {
        return next(req);
    }

    // Log API activity
    console.log('[Interceptor] API Call Detected:', req.url);
    const storedExpire = localStorage.getItem('expireTime');
    const expireTime = (storedExpire && !isNaN(Number(storedExpire))) ? Number(storedExpire) : 300;
    const timeout = (expireTime - 45) * 1000;
    console.log(expireTime);

    // refresh every few mins based on token validity

    const lastRefresh = Number(localStorage.getItem('lastTokenRefresh') ?? '0');
    const now = Date.now();
    // We ignore isNearExpiry because it breaks if the server clock is out of sync with the laptop.
    // Instead, we rely purely on our local 'isTimerUp' and the backend's 401 Unauthorized responses.
    const isNearExpiry = false;
    const isTimerUp = now - lastRefresh > timeout;

    let prepareRequest: Observable<any> = of(null);

    // proactively refresh if near expiry OR refresh explicitly needed
    if (authService.refreshToken && (isNearExpiry || isTimerUp || !authService.accessToken)) {
        prepareRequest = authService.refreshTokenIfActive().pipe(
            catchError(() => of(null)) // on proactive fail, still try request (might recover on 401 retry)
        );
    }

    return prepareRequest.pipe(
        switchMap(() => {
            // Clone the request object
            let newReq = req.clone();

            // Request
            if (authService.accessToken) {
                if (req.headers.has('swagger')) {
                    newReq = req.clone({
                        headers: req.headers.delete('cookie'),
                        url: req.url.replace(
                            'http://gateway.fisheriesdept.gov.lk',
                            'https://api.fisheriesdept.gov.lk'
                        ),
                    });
                } else {
                    newReq = req.clone({
                        headers: req.headers
                            .delete('cookie')
                            .set(
                                'Authorization',
                                'Bearer ' + authService.accessToken
                            ),
                    });
                }
            }

            // Response
            return next(newReq).pipe(
                catchError((error) => {
                    // Catch "401 Unauthorized" responses
                    if (
                        error instanceof HttpErrorResponse &&
                        error.status === 401 &&
                        !req.url.includes(tokenUrl) // Don't retry if refresh itself failed
                    ) {
                        // Refresh and retry
                        return authService.refreshTokenIfActive().pipe(
                            switchMap(() => {
                                const retryReq = req.clone({
                                    headers: req.headers
                                        .delete('Authorization')
                                        .set('Authorization', 'Bearer ' + authService.accessToken)
                                });
                                return next(retryReq);
                            }),
                            catchError((refreshErr) => {
                                console.error('Final logout due to refresh failure after 401', refreshErr);
                                authService.signOut().subscribe();
                                return throwError(() => error);
                            })
                        );
                    }

                    return throwError(error);
                })
            );
        })
    );
};
